import { SearchableSuperType, SearchableType } from './ISearchable.js';
import { fromAoSData } from '../../models/IAbility.js';
import { makeSearchableAbility } from './ISearchableAbility.js';
import { ModeOfPlay } from '../../models/IBattlePack.js';
import { ScourgeType } from '../../models/IScourge.js';
export const RuleSuperType = 'Rule';
export var RuleType;
(function (RuleType) {
    RuleType[RuleType["Document"] = 0] = "Document";
    RuleType[RuleType["Section"] = 1] = "Section";
    RuleType[RuleType["FAQSection"] = 2] = "FAQSection";
    RuleType[RuleType["FAQRule"] = 3] = "FAQRule";
})(RuleType || (RuleType = {}));
// Search id for a rule section. Sections are keyed by their owning document
// (`source` is the document title) AND section name, so identically named sections
// in different documents stay distinct. Without this, e.g. the "1.0 Terrain
// Overview" section in both the GHB 2025-26 and GHB 2026-27 advanced rules would
// share an id and get deduplicated into a single search result. The section and its
// abilities use the same id so they still collapse into one result within a document.
export const ruleSectionSearchId = (source, sectionName) => `${source}::${sectionName}`;
const RULE_SNIPPET_RADIUS = 72;
const cleanRuleSearchText = (text) => {
    return text.replace(/[*^]/g, '').replace(/\s+/g, ' ').trim();
};
const toSnippet = (text, matchIndex, matchLength = 0) => {
    const normalized = cleanRuleSearchText(text);
    if (!normalized)
        return '';
    if (matchIndex === undefined || matchIndex < 0)
        return normalized;
    const start = Math.max(0, matchIndex - RULE_SNIPPET_RADIUS);
    const end = Math.min(normalized.length, matchIndex + matchLength + RULE_SNIPPET_RADIUS);
    const prefix = start > 0 ? '...' : '';
    const suffix = end < normalized.length ? '...' : '';
    return `${prefix}${normalized.slice(start, end).trim()}${suffix}`;
};
const findTextSnippet = (text, value, isRegex) => {
    const normalized = cleanRuleSearchText(text);
    if (!normalized)
        return null;
    if (isRegex) {
        const regex = new RegExp(value);
        const match = regex.exec(normalized.toLowerCase());
        if (!match || match.index === undefined)
            return null;
        return toSnippet(normalized, match.index, match[0].length);
    }
    const index = normalized.toLowerCase().indexOf(value.toLowerCase());
    if (index < 0)
        return null;
    return toSnippet(normalized, index, value.length);
};
export const findSearchableRuleSnippet = (searchable, value, isRegex = false) => {
    if (searchable.data.type === RuleType.Section) {
        const section = searchable.rule;
        for (const container of section.containers) {
            const containerMatch = findTextSnippet(container.name, value, isRegex);
            if (containerMatch)
                return containerMatch;
            for (const component of container.components) {
                const titleMatch = component.title
                    ? findTextSnippet(component.title, value, isRegex)
                    : null;
                if (titleMatch)
                    return titleMatch;
                const textMatch = component.text ? findTextSnippet(component.text, value, isRegex) : null;
                if (textMatch)
                    return textMatch;
                if (component.ability) {
                    const timingMatch = findTextSnippet(component.ability.timing, value, isRegex);
                    if (timingMatch)
                        return timingMatch;
                    const declareMatch = component.ability.declare
                        ? findTextSnippet(component.ability.declare, value, isRegex)
                        : null;
                    if (declareMatch)
                        return declareMatch;
                    const effectMatch = findTextSnippet(component.ability.effect, value, isRegex);
                    if (effectMatch)
                        return effectMatch;
                }
            }
        }
    }
    if (searchable.data.type === RuleType.FAQSection) {
        const section = searchable.rule;
        const titleMatch = findTextSnippet(section.title, value, isRegex);
        if (titleMatch)
            return titleMatch;
        for (const question of section.questions) {
            const questionMatch = findTextSnippet(question.question, value, isRegex);
            if (questionMatch)
                return questionMatch;
            const answerMatch = findTextSnippet(question.answer, value, isRegex);
            if (answerMatch)
                return answerMatch;
        }
        for (const rule of section.rules) {
            const ruleTitleMatch = findTextSnippet(rule.title, value, isRegex);
            if (ruleTitleMatch)
                return ruleTitleMatch;
            for (const question of rule.questions) {
                const questionMatch = findTextSnippet(question.question, value, isRegex);
                if (questionMatch)
                    return questionMatch;
                const answerMatch = findTextSnippet(question.answer, value, isRegex);
                if (answerMatch)
                    return answerMatch;
            }
        }
    }
    if (searchable.data.type === RuleType.FAQRule) {
        const rule = searchable.rule;
        const titleMatch = findTextSnippet(rule.title, value, isRegex);
        if (titleMatch)
            return titleMatch;
        for (const question of rule.questions) {
            const questionMatch = findTextSnippet(question.question, value, isRegex);
            if (questionMatch)
                return questionMatch;
            const answerMatch = findTextSnippet(question.answer, value, isRegex);
            if (answerMatch)
                return answerMatch;
        }
    }
    return null;
};
export const makeSearchableRule = (rule, source) => {
    let searchable;
    if (rule.title) {
        searchable = {
            name: rule.title,
            id: rule.title,
            superType: RuleSuperType,
            type: RuleType.Document,
            abilities: [],
        };
    }
    else {
        const section = rule;
        const abilities = section.containers
            .flatMap((container) => container.components.flatMap((component) => {
            if (component.ability)
                return fromAoSData(component.ability);
            return null;
        }))
            .filter((a) => a !== null);
        searchable = {
            name: section.name,
            id: ruleSectionSearchId(source, section.name),
            superType: RuleSuperType,
            type: RuleType.Section,
            abilities: abilities,
        };
    }
    return {
        superType: SearchableSuperType,
        type: SearchableType.Rule,
        data: searchable,
        armyName: 'Core',
        source: source,
        rule: rule,
        keywords: [],
        legends: false,
        scourge: ScourgeType.None,
        modeOfPlay: rule.modeOfPlay,
    };
};
export const makeSearchableRuleAbilities = (rule, source) => {
    const section = rule;
    const abilities = section.containers
        .flatMap((container) => container.components.flatMap((component) => {
        if (component.ability)
            return fromAoSData(component.ability);
        return null;
    }))
        .filter((a) => a !== null);
    const coreRule = {
        name: section.name,
        id: ruleSectionSearchId(source, section.name),
        superType: RuleSuperType,
        type: RuleType.Section,
        abilities: abilities,
    };
    return abilities.map((ability) => makeSearchableAbility(coreRule, ability, source, 'Rule', ScourgeType.None, rule.modeOfPlay));
};
export const makeSearchableFAQs = (faq) => {
    return faq.data.flatMap((section) => {
        const coreRule = {
            name: section.title,
            id: section.title,
            superType: RuleSuperType,
            type: RuleType.FAQSection,
            abilities: [],
        };
        // each rule in the section becomes a searchable FAQRule
        const searchables = section.rules.map((rule) => {
            const faqRuleCore = {
                name: rule.title,
                id: rule.title,
                superType: RuleSuperType,
                type: RuleType.FAQRule,
                abilities: [],
            };
            return {
                superType: SearchableSuperType,
                type: SearchableType.FAQ,
                data: faqRuleCore,
                armyName: 'Core',
                source: 'FAQ',
                rule: rule,
                keywords: [],
                legends: false,
                scourge: ScourgeType.None,
                modeOfPlay: ModeOfPlay.Unknown,
            };
        });
        searchables.push({
            superType: SearchableSuperType,
            type: SearchableType.FAQ,
            data: coreRule,
            armyName: 'Core',
            source: 'FAQ',
            rule: section,
            keywords: [],
            legends: false,
            scourge: ScourgeType.None,
            modeOfPlay: ModeOfPlay.Unknown,
        });
        return searchables;
    });
};
